/**
 * authFetch — single source of truth for raw fetch authentication headers.
 *
 * Always sends BOTH Authorization (JWT) and X-CBO-ID (active org) so the
 * backend's get_current_cbo() resolver doesn't fall through to the JWT's
 * default org. Without X-CBO-ID, super_admins/multi-org users see data from
 * a different org than the one they switched to in the picker.
 *
 * Use this anywhere you'd otherwise write:
 *
 *   fetch(url, { headers: { Authorization: `Bearer ${token}` } })
 *
 * Replace with:
 *
 *   authFetch(url)                                // GET, no body
 *   authFetch(url, { method: 'POST', body: ... }) // any other method
 *
 * For JSON bodies, you can pass `jsonBody`:
 *
 *   authFetch(url, { method: 'POST', jsonBody: { ... } })
 *
 * For multipart/form-data, pass `body: formData` and DO NOT set Content-Type
 * (the browser will set the boundary).
 */

export interface AuthFetchOptions extends Omit<RequestInit, 'body'> {
  body?: BodyInit | null;
  /** Convenience: serializes to JSON and sets Content-Type. */
  jsonBody?: unknown;
  /** Skip the X-CBO-ID header (use only for org-creation or cross-org admin endpoints). */
  skipCboHeader?: boolean;
}

// ---- Auth token storage --------------------------------------------------------
// On the Tauri desktop app the JWT is kept in the OS keychain (NOT plaintext
// localStorage). buildAuthHeaders is synchronous, so we read from an in-memory
// cache primed at boot by primeAuthToken(). The web build keeps using localStorage.
const isTauri = (): boolean => typeof window !== 'undefined' && '__TAURI__' in window;
const TOKEN_ACCOUNT = 'current';
let tokenCache: string | null = null;

async function tauriInvoke<T>(cmd: string, args?: Record<string, unknown>): Promise<T> {
  const { invoke } = await import('@tauri-apps/api/core');
  return invoke<T>(cmd, args);
}

/** Load the token into the in-memory cache at app boot. On desktop, migrates any
 *  legacy localStorage token into the keychain. Call once early in startup. */
export async function primeAuthToken(): Promise<void> {
  if (isTauri()) {
    try {
      let t = (await tauriInvoke<string | null>('secure_get_token', { account: TOKEN_ACCOUNT })) ?? null;
      if (!t) {
        // Migrate a legacy localStorage token into the keychain (kept in both for
        // now; Phase 4 sweeps the ~40 direct readers, then drops the localStorage copy).
        const legacy = localStorage.getItem('auth_token');
        if (legacy) {
          await tauriInvoke('secure_store_token', { account: TOKEN_ACCOUNT, token: legacy });
          t = legacy;
        }
      }
      tokenCache = t ?? localStorage.getItem('auth_token');
    } catch {
      tokenCache = localStorage.getItem('auth_token');
    }
  } else {
    tokenCache = localStorage.getItem('auth_token');
  }
}

/** Persist the JWT + update the cache. Desktop dual-writes to the keychain AND
 *  localStorage during migration (Phase 4 removes the localStorage copy). */
export async function setAuthToken(token: string): Promise<void> {
  tokenCache = token;
  if (isTauri()) {
    try { await tauriInvoke('secure_store_token', { account: TOKEN_ACCOUNT, token }); } catch { /* ignore */ }
  }
  localStorage.setItem('auth_token', token);
}

/** Clear the JWT from all stores + cache (call on logout). */
export async function clearAuthToken(): Promise<void> {
  tokenCache = null;
  if (isTauri()) {
    try { await tauriInvoke('secure_delete_token', { account: TOKEN_ACCOUNT }); } catch { /* ignore */ }
  }
  try { localStorage.removeItem('auth_token'); } catch { /* ignore */ }
}

/**
 * Forget the in-memory copy only (TRUS-638). For a window that learns another
 * window signed out: that window already cleared the keychain and localStorage,
 * and clearing the keychain again from here could race a fresh sign-in.
 */
export function dropCachedAuthToken(): void {
  tokenCache = null;
}

/** Current JWT (cache first, localStorage fallback for web / pre-prime). */
export function getAuthToken(): string | null {
  if (tokenCache) return tokenCache;
  return typeof localStorage !== 'undefined' ? localStorage.getItem('auth_token') : null;
}

/** Build the standard auth headers (Authorization + X-CBO-ID). */
export function buildAuthHeaders(extra?: HeadersInit, skipCbo = false): Headers {
  const h = new Headers(extra || {});
  const token = getAuthToken();
  if (token && !h.has('Authorization')) {
    h.set('Authorization', `Bearer ${token}`);
  }
  if (!skipCbo) {
    const cboId = localStorage.getItem('active_cbo_id');
    if (cboId && !h.has('X-CBO-ID')) {
      h.set('X-CBO-ID', cboId);
    }
  }
  return h;
}

/**
 * Resolve a relative `/api/...` path against the configured backend host.
 *
 * - When `VITE_WEAVEIN_API_URL` is unset, fall back to the prod backend URL.
 * - When it is the empty string `""` (the dev Vercel deployment), preserve
 *   relative URLs so the dev `vercel.json` proxy can rewrite them.
 *   Use `??` (not `||`) so the empty string is preserved — see
 *   reference_render_services.md "?? vs ||" critical note.
 * - When the caller passes an absolute URL (http(s)://...) leave it alone.
 */
// Use the product-aware base URL so Triveny → ms-api-hots and WeaveIn →
// weavein-api. Direct VITE_WEAVEIN_API_URL reads were hitting the WeaveIn
// backend on Triveny → platform JWTs 401'd as "Invalid token".
import { config as productConfig } from '@/src/config';
import { checkSessionTokenPresent, SessionEndedError } from '@/lib/sessionBroadcast';
import { handleImpersonationResponse, isImpersonating } from '@/lib/impersonation';
const WEAVEIN_API_URL = (productConfig.weaveinApi?.baseUrl ?? 'https://weavein-api.onrender.com').trim();

function resolveUrl(url: string): string {
  if (/^https?:\/\//i.test(url)) return url;
  if (!WEAVEIN_API_URL) return url; // dev Vercel — proxy handles it
  if (url.startsWith('/')) return `${WEAVEIN_API_URL}${url}`;
  return url;
}

/** Drop-in replacement for `fetch(url, ...)` that always includes auth + active org. */
export async function authFetch(url: string, options: AuthFetchOptions = {}): Promise<Response> {
  const { jsonBody, skipCboHeader, headers, body, ...rest } = options;

  // Signed in here, but the token is gone: another window signed out. Sign this
  // window out too rather than sending a request that cannot succeed (TRUS-638).
  if (!checkSessionTokenPresent()) throw new SessionEndedError();

  const finalHeaders = buildAuthHeaders(headers, skipCboHeader);
  let finalBody: BodyInit | null | undefined = body;

  if (jsonBody !== undefined) {
    if (!finalHeaders.has('Content-Type')) {
      finalHeaders.set('Content-Type', 'application/json');
    }
    finalBody = JSON.stringify(jsonBody);
  }

  const res = await fetch(resolveUrl(url), { ...rest, headers: finalHeaders, body: finalBody });
  // TRUS-747: under View as user, a 401 ends the session (back to the admin) and
  // a 403 may be the session policy (shown in the banner). Response unchanged.
  if ((res.status === 401 || res.status === 403) && isImpersonating()) {
    res
      .clone()
      .json()
      .then((data) => handleImpersonationResponse(res.status, data, url))
      .catch(() => {});
  }
  return res;
}

/** Sugar for the common JSON-GET case: returns parsed JSON or throws on non-2xx. */
export async function authFetchJson<T = unknown>(
  url: string,
  options: AuthFetchOptions = {},
): Promise<T> {
  const res = await authFetch(url, options);
  if (!res.ok) {
    let detail: string | undefined;
    try {
      const data = await res.json();
      detail = data?.detail || data?.message;
    } catch {
      // ignore
    }
    // Report genuine server faults (5xx) the user hit to the observability agent.
    // Dynamic import avoids a static cycle (activityTracker imports authFetch).
    if (res.status >= 500) {
      import('@/src/lib/activityTracker')
        .then((m) =>
          m.trackError({
            error_type: 'HttpError',
            error_message: detail || `${res.status} ${res.statusText}`,
            status_code: res.status,
            method: (options.method || 'GET').toUpperCase(),
            path: url,
          }),
        )
        .catch(() => {});
    }
    throw new Error(detail || `${res.status} ${res.statusText}`);
  }
  return (await res.json()) as T;
}

/**
 * The same base-URL resolution, with NO credentials attached.
 *
 * For surfaces that are deliberately unauthenticated — a doula answering a
 * match offer from an emailed link, who may have no account at all. The signed
 * token in the body is the whole authority there, so sending an Authorization
 * header or the active-org header would be meaningless at best; at worst it
 * answers the offer as whoever happens to be logged in on that browser.
 *
 * It shares `resolveUrl` on purpose. A page that rebuilt the base URL itself
 * would keep working in dev, where Vercel proxies, and silently post to the
 * wrong origin in production.
 */
export async function publicFetchJson<T = unknown>(
  url: string,
  options: Omit<RequestInit, 'body'> & { jsonBody?: unknown } = {},
): Promise<T> {
  const { jsonBody, headers, ...rest } = options;
  const finalHeaders = new Headers(headers);
  let body: BodyInit | undefined;
  if (jsonBody !== undefined) {
    if (!finalHeaders.has('Content-Type')) finalHeaders.set('Content-Type', 'application/json');
    body = JSON.stringify(jsonBody);
  }

  const res = await fetch(resolveUrl(url), { ...rest, headers: finalHeaders, body });
  if (!res.ok) {
    let detail: string | undefined;
    try {
      const data = await res.json();
      detail = data?.detail || data?.message;
    } catch {
      // ignore
    }
    throw new Error(detail || `${res.status} ${res.statusText}`);
  }
  return (await res.json()) as T;
}
